'use client';

import type { ComponentType, ReactNode, SVGProps } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import {
  Building2,
  Calendar,
  ExternalLink,
  FileDown,
  FileText,
  FolderOpen,
  Inbox,
  Layers,
  ListChecks,
  Loader2,
  ScrollText,
  Tag,
  User
} from 'lucide-react';

import { useRouter } from '@/i18n/routing';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { registrationQueries } from '@/features/registration';
import { stickingQueries } from '@/features/sticking';
import { DOCUMENT_TYPES } from '../constants';

interface ContainerDocumentsDialogProps {
  registrationId: number | null;
  containerNumber: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

export function ContainerDocumentsDialog({
  registrationId,
  containerNumber,
  open,
  onOpenChange
}: ContainerDocumentsDialogProps) {
  const t = useTranslations('search');
  const router = useRouter();
  const enabled = open && registrationId != null;

  const { data: containerResp, isPending: loadingContainer } = useQuery({
    ...registrationQueries.detail(registrationId ?? 0),
    enabled
  });

  const { data: documents, isPending: loadingDocs } = useQuery({
    ...stickingQueries.pageIndexes(registrationId ?? 0, { per_page: 100 }),
    enabled
  });

  const container = containerResp?.data;
  const docs = documents?.data ?? [];
  const entityName = container?.entity_name_ar || container?.entity_name_en;
  const dateFrom = container?.date_from ?? null;
  const dateTo = container?.date_to ?? null;
  const dateRange = formatDateRange(dateFrom, dateTo);
  const stageKey = container?.current_stage;
  const stageLabel = stageKey
    ? t.has(`stage.${stageKey}`)
      ? t(`stage.${stageKey}`)
      : stageKey
    : null;
  const statusKey = container?.status;
  const statusLabel =
    statusKey && t.has(`status.${statusKey}`) ? t(`status.${statusKey}`) : null;
  const priorityKey = container?.priority;
  const priorityLabel =
    priorityKey && t.has(`priority.${priorityKey}`)
      ? t(`priority.${priorityKey}`)
      : null;
  const pdfUrl = container?.latest_scan_pdf_url ?? null;
  const docCount = container?.page_indexes_count ?? docs.length;

  // Only documents that carry real information are worth listing. A page with
  // no title, no document number, and no date is just a placeholder row — the
  // count is more honest than ten "Untitled — page N" lines.
  const informativeDocs = docs.filter(
    (d) => !!(d.title_ar || d.title_en || d.document_number || d.document_date)
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'flex h-[min(85dvh,720px)] w-full max-w-2xl flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl'
        )}
      >
        {/* Header — fixed */}
        <DialogHeader className='shrink-0 space-y-2 border-b px-6 py-5 pe-12 text-start'>
          <div className='flex flex-wrap items-center gap-2'>
            <FolderOpen className='text-primary size-5 shrink-0' />
            <DialogTitle className='text-base font-semibold sm:text-lg'>
              {containerNumber ?? container?.container_number ?? '—'}
            </DialogTitle>
            {stageLabel && (
              <Badge variant='secondary' className='text-[11px] font-medium'>
                {stageLabel}
              </Badge>
            )}
            {statusLabel && (
              <Badge
                variant={statusKey === 'closed' ? 'outline' : 'default'}
                className='text-[11px] font-medium'
              >
                {statusLabel}
              </Badge>
            )}
          </div>
          <DialogDescription className='text-muted-foreground text-xs'>
            {t('containerDetailsHint')}
          </DialogDescription>
        </DialogHeader>

        {/* Body — scrollable */}
        <div className='flex-1 space-y-6 overflow-y-auto px-6 py-5'>
          {/* Details */}
          {loadingContainer ? (
            <div className='text-muted-foreground flex items-center gap-2 py-4 text-sm'>
              <Loader2 className='size-4 animate-spin' />
              {t('loading')}
            </div>
          ) : container ? (
            <dl className='grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2 lg:grid-cols-3'>
              {container.client?.name && (
                <Detail
                  icon={User}
                  label={t('detail.client')}
                  value={container.client.name}
                />
              )}
              {container.container_type?.name && (
                <Detail
                  icon={Tag}
                  label={t('detail.containerType')}
                  value={container.container_type.name}
                />
              )}
              {container.dynamic_form?.name && (
                <Detail
                  icon={ScrollText}
                  label={t('detail.formName')}
                  value={container.dynamic_form.name}
                />
              )}
              {entityName && (
                <Detail
                  icon={Building2}
                  label={t('detail.entity')}
                  value={entityName}
                />
              )}
              {priorityLabel && (
                <Detail
                  icon={Layers}
                  label={t('detail.priority')}
                  value={priorityLabel}
                />
              )}
              {container.actual_page_count != null && (
                <Detail
                  icon={ScrollText}
                  label={t('detail.pages')}
                  value={container.actual_page_count.toLocaleString()}
                />
              )}
              {docCount > 0 && (
                <Detail
                  icon={ListChecks}
                  label={t('detail.documents')}
                  value={docCount.toLocaleString()}
                />
              )}
              {dateRange && (
                <Detail
                  icon={Calendar}
                  label={t('detail.dateRange')}
                  value={dateRange}
                />
              )}
              {container.created_at && (
                <Detail
                  icon={Calendar}
                  label={t('detail.created')}
                  value={container.created_at.slice(0, 10)}
                />
              )}
              {container.creator?.name && (
                <Detail
                  icon={User}
                  label={t('detail.creator')}
                  value={container.creator.name}
                />
              )}
              {container.notes && (
                <div className='sm:col-span-2 lg:col-span-3'>
                  <Detail
                    icon={FileText}
                    label={t('detail.notes')}
                    value={
                      <span className='text-foreground/90 line-clamp-3 text-sm font-normal whitespace-pre-line'>
                        {container.notes}
                      </span>
                    }
                  />
                </div>
              )}
            </dl>
          ) : null}

          {/* Indexed documents — only listed when they carry real metadata. */}
          {loadingDocs ? (
            <section>
              <h4 className='mb-2 text-sm font-semibold'>
                {t('documentsHeading')}
              </h4>
              <div className='text-muted-foreground flex items-center gap-2 py-4 text-sm'>
                <Loader2 className='size-4 animate-spin' />
                {t('loading')}
              </div>
            </section>
          ) : docs.length === 0 ? (
            <section>
              <h4 className='mb-2 text-sm font-semibold'>
                {t('documentsHeading')}
              </h4>
              <div className='text-muted-foreground flex flex-col items-center gap-2 rounded-md border border-dashed py-6 text-center text-xs'>
                <Inbox className='size-5 opacity-60' />
                <span>{t('noDocuments')}</span>
              </div>
            </section>
          ) : informativeDocs.length === 0 ? (
            // Pages exist but none have titles/numbers/dates yet — collapse to
            // a single line. Listing 20× "Untitled — page N" is noise.
            <section className='bg-muted/30 text-muted-foreground flex items-center gap-2 rounded-md border px-3 py-2.5 text-xs'>
              <FileText className='size-4 shrink-0' />
              <span>{t('pagesPendingIndexing', { count: docs.length })}</span>
            </section>
          ) : (
            <section>
              <div className='mb-2 flex items-center justify-between'>
                <h4 className='text-sm font-semibold'>
                  {t('documentsHeading')}
                </h4>
                <span className='text-muted-foreground text-xs'>
                  {informativeDocs.length.toLocaleString()}
                  {docs.length !== informativeDocs.length &&
                    ` / ${docs.length.toLocaleString()}`}
                </span>
              </div>
              <ul className='divide-border border-border divide-y rounded-md border'>
                {informativeDocs.map((doc) => {
                  const docType = (
                    DOCUMENT_TYPES as readonly string[]
                  ).includes(doc.document_type)
                    ? t(`docType.${doc.document_type}`)
                    : null;
                  const title =
                    doc.title_ar || doc.title_en || t('untitledDocument');
                  return (
                    <li
                      key={doc.id}
                      className='hover:bg-muted/40 flex items-start gap-3 px-3 py-2.5 transition-colors'
                    >
                      <FileText className='text-muted-foreground mt-0.5 size-4 shrink-0' />
                      <div className='flex min-w-0 flex-1 flex-col gap-1'>
                        <span className='truncate text-sm'>{title}</span>
                        <div className='text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 text-xs'>
                          {docType && (
                            <Badge variant='outline' className='text-[10px]'>
                              {docType}
                            </Badge>
                          )}
                          <span>
                            {t('pageLocationShort', { page: doc.page_number })}
                          </span>
                          {doc.document_number && (
                            <span className='truncate'>
                              {doc.document_number}
                            </span>
                          )}
                          {doc.document_date && (
                            <span>{doc.document_date}</span>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </div>

        {/* Footer — fixed */}
        <div className='bg-background flex shrink-0 flex-col-reverse gap-2 border-t px-6 py-4 sm:flex-row sm:justify-end'>
          {pdfUrl && (
            <Button asChild variant='outline'>
              <a href={pdfUrl} target='_blank' rel='noreferrer noopener'>
                <FileDown className='me-2 size-4' />
                {t('viewPdf')}
              </a>
            </Button>
          )}
          {registrationId != null && (
            <Button
              onClick={() => {
                onOpenChange(false);
                router.push(`/dashboard/containers/${registrationId}`);
              }}
            >
              <ExternalLink className='me-2 size-4' />
              {t('openContainer')}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Detail({
  icon: Icon,
  label,
  value
}: {
  icon: Icon;
  label: string;
  value: ReactNode;
}) {
  return (
    <div className='flex items-start gap-2.5'>
      <Icon className='text-muted-foreground mt-0.5 size-3.5 shrink-0' />
      <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
        <dt className='text-muted-foreground text-[11px] font-medium tracking-wide uppercase'>
          {label}
        </dt>
        <dd className='truncate text-sm font-medium'>{value}</dd>
      </div>
    </div>
  );
}

function formatDateRange(
  from: string | null,
  to: string | null
): string | null {
  if (!from && !to) return null;
  const trim = (d: string | null) => (d ? d.slice(0, 10) : '—');
  if (from && to) return `${trim(from)} — ${trim(to)}`;
  return trim(from ?? to);
}
